import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MetaHead } from '../components/MetaHead';
import { COMPANY_INFO } from '../data/scrapedData';

export function DobbeltdiagnosePage() {
  const [formSubmitted, setFormSubmitted] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [formError, setFormError] = React.useState(null);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);

    const formData = new FormData(e.currentTarget);
    const payload = {
      name: formData.get('name'),
      phone: formData.get('phone'),
      email: formData.get('email'),
      message: formData.get('message'),
      website_url: formData.get('website_url') || '',
    };

    try {
      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setFormSubmitted(true);
        e.target.reset();
      } else {
        const data = await res.json().catch(() => null);
        setFormError(data?.error || 'Der opstod en fejl. Prøv venligst igen senere.');
      }
    } catch {
      setFormError('Kunne ikke sende formularen. Tjek din internetforbindelse og prøv igen.');
    } finally {
      setSubmitting(false);
    }
  };

  const treatmentComponents = [
    {
      number: "01",
      title: "Afgiftning og primærbehandling",
      desc: "Når det indgår i det vurderede behov, etableres skånsom afgiftning og efterfølgende primærbehandling i rolige rammer.",
      linkText: "Læs om afgiftning",
      path: "/behandling/afgiftning",
      secondaryLink: { text: "Primærbehandling", path: "/behandling/primaerbehandling" },
      icon: "fa-solid fa-heart-pulse"
    },
    {
      number: "02",
      title: "Dagbehandling eller døgnbehandling",
      desc: "Fast struktur og faglig støtte tilpasset den enkeltes funktionsniveau og situation – enten med bopæl på Midtgaarden eller i dagtimerne.",
      linkText: "Se døgnbehandling",
      path: "/doegnbehandling",
      secondaryLink: { text: "Dagbehandling", path: "/behandling/dagbehandling" },
      icon: "fa-solid fa-house-chimney-medical"
    },
    {
      number: "03",
      title: "Tilbagefaldsforebyggelse",
      desc: "Målrettet fokus på personlige advarselstegn, risikosituationer, triggere og opbygning af nye handlemåder i hverdagen.",
      linkText: "Læs om tilbagefald",
      path: "/behandling/tilbagefaldsbehandling-og-forebyggelse",
      icon: "fa-solid fa-shield-halved"
    },
    {
      number: "04",
      title: "Efterværn og udslusning",
      desc: "Samarbejde med kommune og myndighed for at sikre en sammenhængende overgang og tryg videre vej via f.eks. Halvvejshuset i Esbjerg.",
      linkText: "Halvvejshus & udslusning",
      path: "/behandling/eftervaern-og-udslusning",
      icon: "fa-solid fa-house-user"
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Behandling af dobbeltdiagnose | Midtgaarden – et tilbud til mennesker i misbrug",
    "description": "Hvad er dobbeltdiagnose, og hvilken behandling er relevant? Læs om komorbiditet, misbrug og mulighederne for dag- og døgnbehandling hos Midtgaarden.",
    "publisher": {
      "@type": "Organization",
      "name": "Den Selvejende Institution Midtgaarden",
      "url": "https://www.midtgaarden.dk"
    }
  };

  return (
    <>
      <MetaHead
        title="Behandling af dobbeltdiagnose | Midtgaarden – et tilbud til mennesker i misbrug"
        description="Hvad er dobbeltdiagnose, og hvilken behandling er relevant? Læs om komorbiditet, misbrug og mulighederne for dag- og døgnbehandling hos Midtgaarden."
        ogImage="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/personer-forsyner-sig-fra-buffet-ved-spisebord.jpg"
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      {/* HERO SECTION */}
      <section className="relative bg-[#1C1C1E] text-white py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/personer-forsyner-sig-fra-buffet-ved-spisebord.jpg"
            alt="Personer forsyner sig med mad fra en anrettet buffet ved et spisebord"
            className="w-full h-full object-cover object-center opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C1C1E] via-[#1C1C1E]/90 to-[#1C1C1E]/75" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 [text-shadow:_0_2px_12px_rgba(0,0,0,0.85)]">
          <div className="max-w-3xl space-y-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#3885B8] text-white">
                <i className="fa-solid fa-brain text-[11px]" aria-hidden="true" />
                Dobbeltdiagnose
              </span>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-white/90 backdrop-blur-xs">
                Psykisk sårbarhed & misbrug
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Behandling af dobbeltdiagnose med blik for hele mennesket
            </h1>

            <div className="space-y-4 text-base sm:text-lg md:text-xl text-white/90 leading-relaxed font-normal">
              <p>
                Hos Midtgaarden tilbyder vi behandling af dobbeltdiagnose til voksne, som lever med et stofmisbrug eller alkoholmisbrug og samtidig har en diagnosticeret eller mulig psykisk problemstilling. Fra Sejstrup nær Esbjerg tilbyder vi individuelt tilrettelagte behandlingsforløb for borgere fra hele landet.
              </p>
            </div>

            {/* CTA action buttons */}
            <div className="pt-2 flex flex-wrap items-center gap-3 sm:gap-4">
              <a
                href={COMPANY_INFO.phoneTel}
                className="h-14 px-8 rounded-full bg-[#3885B8] hover:bg-[#2A6E9B] active:bg-[#1F557B] text-white font-bold text-base shadow-lg transition-all duration-200 inline-flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3885B8]"
              >
                <i className="fa-solid fa-phone text-base" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring: {COMPANY_INFO.phone}</span>
              </a>

              <a
                href={COMPANY_INFO.emailMailto}
                aria-label="Send e-mail til Midtgaarden"
                className="w-14 h-14 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white flex items-center justify-center transition-all duration-200 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white shrink-0"
              >
                <i className="fa-solid fa-envelope text-lg" aria-hidden="true" />
              </a>
            </div>

            {/* Trust highlights */}
            <div className="pt-6 flex flex-wrap justify-between items-center gap-y-3 border-t border-white/15 text-xs sm:text-sm text-white/80">
              <div className="flex items-center gap-2 shrink-0">
                <i className="fa-solid fa-check text-[#3885B8]" aria-hidden="true" />
                <span>20+ års erfaring</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <i className="fa-solid fa-check text-[#3885B8]" aria-hidden="true" />
                <span>Cenaps-modellen</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <i className="fa-solid fa-check text-[#3885B8]" aria-hidden="true" />
                <span>Helhedsorienteret</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <i className="fa-solid fa-check text-[#3885B8]" aria-hidden="true" />
                <span>Dag- og døgntilbud</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 1: HVAD ER DOBBELTDIAGNOSE & KOMORBIDITET (SAMLET) */}
      <section id="hvad-er-dobbeltdiagnose" className="py-16 sm:py-24 bg-white border-b border-[#1C1C1E]/10 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            
            {/* Left column: Dobbeltdiagnose og Komorbiditet */}
            <div className="lg:col-span-7 space-y-10">
              
              {/* Del 1: Hvad er dobbeltdiagnose? */}
              <div>
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8] block mb-2">
                  DEFINITION OG SAMMENHÆNG
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
                  Hvad er dobbeltdiagnose?
                </h2>
                <div className="w-16 h-1 bg-[#3885B8] my-4" />
                <div className="space-y-4 text-base sm:text-lg text-[#1C1C1E]/80 leading-relaxed pt-1">
                  <p>
                    Hvad er dobbeltdiagnose? Begrebet bruges typisk om en situation, hvor en person både har en psykisk lidelse og et skadeligt forbrug eller en afhængighed af alkohol eller stoffer. Det er altså ikke blot to problemer, der eksisterer side om side. De forskellige udfordringer kan påvirke hinanden og gøre det sværere at skabe forandring.
                  </p>
                  <p>
                    Rusmidler kan eksempelvis påvirke den psykiske trivsel, mens psykiske symptomer kan gøre det vanskeligere at ændre et misbrug. Nogle mennesker bruger også alkohol eller stoffer i et forsøg på at håndtere uro, angst, tristhed eller andre former for psykisk ubehag.
                  </p>
                  <p>
                    En dobbeltdiagnose kan være kendt og diagnosticeret på forhånd, men der kan også være tale om en psykisk problemstilling, som endnu ikke er fuldt afklaret. Derfor er det vigtigt at se på den samlede situation, når et behandlingsforløb skal tilrettelægges.
                  </p>
                </div>
              </div>

              {/* Del 2: Hvad er komorbiditet? */}
              <div className="pt-8 border-t border-[#1C1C1E]/10 space-y-6">
                <div>
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8] block mb-2">
                    FAGLIGT BEGREB & HELHED
                  </span>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
                    Hvad er komorbiditet?
                  </h2>
                  <div className="w-16 h-1 bg-[#3885B8] my-4" />
                </div>

                <div className="space-y-4 text-base sm:text-lg text-[#1C1C1E]/80 leading-relaxed">
                  <p>
                    Hvad er komorbiditet? Komorbiditet er en faglig betegnelse for, at en person har flere sygdomme eller lidelser samtidig. Begrebet kan omfatte både fysiske og psykiske lidelser og er derfor bredere end begrebet dobbeltdiagnose.
                  </p>
                  <p>
                    Hvad betyder komorbiditet i forbindelse med misbrug? Når komorbiditet optræder sammen med et misbrug, kan de forskellige problemstillinger påvirke hinanden. Det kan eksempelvis være et samtidigt misbrug og en psykisk lidelse, men komorbiditet kan også dække over andre kombinationer af sygdomme og lidelser.
                  </p>
                  <p>
                    Derfor er komorbiditet og misbrug relevante at se på samlet, når man skal vurdere behovet for behandling. Ikke al komorbiditet er en dobbeltdiagnose, men en dobbeltdiagnose kan forstås som en særlig kombination af samtidige problemstillinger, hvor psykisk sygdom og misbrug optræder sammen.
                  </p>
                </div>

                {/* Subsection: Komorbiditet – betydning for behandlingen */}
                <div className="pt-6 border-t border-[#1C1C1E]/10 space-y-4">
                  <h3 className="text-xl sm:text-2xl font-bold text-[#1C1C1E] tracking-tight">
                    Komorbiditet – betydning for behandlingen
                  </h3>
                  <p className="text-base text-[#1C1C1E]/80 leading-relaxed">
                    Komorbiditetens betydning for et behandlingsforløb afhænger af den enkelte situation. Når flere problemstillinger optræder samtidig, kan det være nødvendigt at tage højde for dem alle frem for kun at fokusere på misbruget.
                  </p>
                  <p className="text-base text-[#1C1C1E]/80 leading-relaxed">
                    Hos Midtgaarden, vores center for{' '}
                    <Link
                      to="/"
                      className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                    >
                      misbrugsbehandling
                    </Link>
                    , ser vi blandt andet på biologiske, psykiske og sociale forhold. Det giver mulighed for at tilrettelægge behandlingen ud fra den enkeltes støttebehov, funktionsniveau og livssituation.
                  </p>
                  <p className="text-base text-[#1C1C1E]/80 leading-relaxed">
                    Andre forhold kan også have betydning for behandlingen, eksempelvis kriminalitet eller ludomani. De er ikke i sig selv en dobbeltdiagnose, men kan indgå i den samlede vurdering af, hvilken støtte og hvilke rammer der er behov for – herunder om forløbet bør kombineres med et midlertidigt{' '}
                    <Link
                      to="/botilbud"
                      className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                    >
                      botilbud
                    </Link>
                    .
                  </p>
                </div>
              </div>

            </div>

            {/* Right column: Explanatory card & imagery */}
            <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-28">
              {/* Begrebsafklaring Card */}
              <div className="bg-[#F8FAFC] rounded-none border border-[#1C1C1E]/10 p-6 sm:p-8 shadow-xs space-y-5">
                <div className="flex items-center gap-3 border-b border-[#1C1C1E]/10 pb-4">
                  <div className="w-10 h-10 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center font-bold text-base shrink-0">
                    <i className="fa-solid fa-scale-balanced" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-bold text-base text-[#1C1C1E]">
                      Faglig begrebsafklaring
                    </h4>
                    <p className="text-xs text-[#1C1C1E]/60">Forstå forskellen</p>
                  </div>
                </div>

                <div className="space-y-4 text-sm">
                  <div className="bg-white p-4 rounded-none border border-[#1C1C1E]/10 border-l-4 border-l-[#3885B8] space-y-1.5 shadow-xs">
                    <span className="font-bold text-xs uppercase tracking-wider text-[#3885B8] block">
                      Dobbeltdiagnose
                    </span>
                    <p className="text-[#1C1C1E]/80">
                      En specifik kombination, hvor en borger <strong>samtidig har en psykisk lidelse</strong> og et <strong>skadeligt misbrug eller afhængighed</strong> af alkohol eller stoffer.
                    </p>
                  </div>

                  <div className="bg-white p-4 rounded-none border border-[#1C1C1E]/10 border-l-4 border-l-[#3885B8] space-y-1.5 shadow-xs">
                    <span className="font-bold text-xs uppercase tracking-wider text-[#3885B8] block">
                      Komorbiditet
                    </span>
                    <p className="text-[#1C1C1E]/80">
                      Det bredere begreb for tilstedeværelsen af <strong>flere samtidige sygdomme eller lidelser</strong> (både fysiske og psykiske) hos samme person.
                    </p>
                  </div>
                </div>

                <div className="pt-2">
                  <Link
                    to="/behandling/bio-psyko-sociale-model"
                    className="inline-flex items-center gap-2 text-xs font-bold text-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                  >
                    <span>Læs om den bio-psyko-sociale model</span>
                    <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                  </Link>
                </div>
              </div>

              {/* Photo from Midtgaarden */}
              <div className="rounded-none overflow-hidden border border-[#1C1C1E]/10 shadow-xl bg-white">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/mand-og-kvinde-i-samtale-i-lyst-samtalerum.jpg"
                  alt="Mand og kvinde i rolig samtale siddende i lænestole i et lyst samtalerum"
                  className="w-full h-56 object-cover rounded-none"
                />
                <div className="p-4 bg-white rounded-none border-t border-[#1C1C1E]/10">
                  <p className="text-xs text-[#1C1C1E]/70 flex items-start gap-2">
                    <i className="fa-solid fa-location-dot text-[#3885B8] mt-0.5 shrink-0" aria-hidden="true" />
                    <span>Midtgaarden i Sejstrup nær Esbjerg tilbyder faste, strukturerede rammer med plads til fordybelse og tryghed.</span>
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 3: MISBRUGSBEHANDLING VED DOBBELTDIAGNOSE */}
      <section className="py-16 sm:py-24 bg-white border-b border-[#1C1C1E]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="max-w-3xl space-y-4">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8] block mb-2">
              FAGLIG INDSATS & METODER
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
              Misbrugsbehandling ved dobbeltdiagnose
            </h2>
            <div className="w-16 h-1 bg-[#3885B8] my-4" />
            <div className="space-y-4 text-base sm:text-lg text-[#1C1C1E]/80 leading-relaxed pt-1">
              <p>
                Misbrugsbehandling ved dobbeltdiagnose kræver ofte tydelige rammer og plads til de forskellige udfordringer, som den enkelte står med. Hos Midtgaarden arbejder vi med individuelt tilrettelagte forløb, hvor borgeren mødes respektfuldt og inddrages i arbejdet med misbrug, mønstre, risikosituationer og nye handlemuligheder.
              </p>
              <p>
                Midtgaardens behandling tager blandt andet afsæt i behandlingsmodellen Cenaps og en helhedsorienteret forståelse af mennesket. Metoder og behandlingsrammer vælges ud fra den enkeltes behov frem for en fast løsning, der skal passe til alle.
              </p>
              <p className="font-semibold text-[#1C1C1E] pt-2">
                Et behandlingsforløb kan blandt andet indeholde:
              </p>
            </div>
          </div>

          {/* Grid of the 4 Treatment Components */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {treatmentComponents.map((item, idx) => (
              <div
                key={idx}
                className="bg-[#F8FAFC] border border-[#1C1C1E]/10 rounded-none p-6 flex flex-col justify-between shadow-xs hover:shadow-md hover:border-[#3885B8]/50 transition-all duration-200"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-extrabold text-[#3885B8]/30 font-mono">
                      {item.number}
                    </span>
                    <div className="w-10 h-10 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-sm">
                      <i className={item.icon} aria-hidden="true" />
                    </div>
                  </div>

                  <h3 className="font-bold text-lg text-[#1C1C1E] leading-snug">
                    {item.title}
                  </h3>

                  <p className="text-xs sm:text-sm text-[#1C1C1E]/75 leading-relaxed">
                    {item.desc}
                  </p>
                </div>

                <div className="pt-6 border-t border-[#1C1C1E]/5 mt-6 flex flex-col gap-2">
                  <Link
                    to={item.path}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                  >
                    <span>{item.linkText}</span>
                    <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                  </Link>
                  {item.secondaryLink && (
                    <Link
                      to={item.secondaryLink.path}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1C1C1E]/60 hover:text-[#3885B8] transition-colors"
                    >
                      <span>{item.secondaryLink.text}</span>
                      <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="max-w-3xl pt-2">
            <p className="text-base text-[#1C1C1E]/80 leading-relaxed">
              De forskellige dele kombineres efter behov, og ikke alle følger samme rækkefølge. Hvis forløbet kræver forudgående{' '}
              <Link
                to="/behandling/afgiftning"
                className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
              >
                afgiftning
              </Link>{' '}
              eller medicinsk udtrapning, sker dette altid under tæt psykiatrisk supervision. Målet er at skabe sammenhæng mellem arbejdet med misbruget, den psykiske sårbarhed og livet uden for behandlingen.
            </p>
          </div>
        </div>
      </section>

      {/* VISUAL BREAK / MELLEMSEKTION MED GLADE MENNESKER & FÆLLESSKAB */}
      <section className="relative overflow-hidden min-h-[380px] sm:min-h-[480px] md:min-h-[540px] lg:min-h-[600px] flex items-end bg-[#1C1C1E]">
        <img
          src="https://assets.auxodev.dk/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/personer-i-munter-samtale-ved-spisebord.jpg"
          alt="Personer samlet omkring et spisebord i munter samtale med grin og kaffekopper"
          className="absolute inset-0 w-full h-full object-cover object-center brightness-95 contrast-[1.03] rounded-none"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1E]/95 via-[#1C1C1E]/45 to-[#1C1C1E]/20" />
        
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 text-white">
          <div className="max-w-2xl space-y-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#3885B8] text-white">
              <i className="fa-solid fa-heart text-[11px]" aria-hidden="true" />
              Fællesskab & tryghed
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight mb-4">
              Et ligeværdigt miljø med plads til grin, nærvær og udvikling
            </h2>
            <div className="w-16 h-1 bg-[#3885B8] my-4" />
            <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-xl">
              Behandling handler ikke kun om samtaler i terapilokalet – det handler også om at genfinde livsglæden, relationerne og et sundt fællesskab i trygge og omsorgsfulde rammer.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4: DØGNBEHANDLING VED DOBBELTDIAGNOSE & DAGBEHANDLING */}
      <section className="py-16 sm:py-24 bg-[#F8FAFC] border-b border-[#1C1C1E]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="max-w-3xl space-y-4">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8] block mb-2">
              RAMMER FOR FORLØBET
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
              Døgnbehandling ved dobbeltdiagnose
            </h2>
            <div className="w-16 h-1 bg-[#3885B8] my-4" />
            <div className="space-y-4 text-base sm:text-lg text-[#1C1C1E]/80 leading-relaxed pt-1">
              <p>
                Døgnbehandling ved dobbeltdiagnose kan være relevant, når der er behov for at bo i trygge rammer og få afstand til miljøer eller situationer, som kan fastholde misbruget. Midtgaardens døgnbehandling er for voksne fra 18 år.
              </p>
              <p>
                I døgnbehandlingen er der mulighed for at arbejde mere intensivt med både afhængighed og samtidige psykiske udfordringer. Den faste struktur kan give ro til at arbejde med behandlingen og med de mønstre og situationer, der har betydning for den enkeltes liv.
              </p>
              <p>
                Når man undersøger mulighederne for døgnbehandling og dobbeltdiagnose, er det vigtigt at se på det samlede støttebehov og ikke kun på selve rusmidlet. Et forløb kan også rumme primærbehandling, tilbagefaldsforebyggelse, efterværn og udslusning, når det passer til den aftalte indsats (se hele vores{' '}
                <Link
                  to="/behandling"
                  className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                >
                  behandlingsoversigt
                </Link>
                ).
              </p>
            </div>
          </div>

          {/* Comparison Cards: Døgnbehandling vs. Dagbehandling */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Card 1: Døgnbehandling */}
            <div className="bg-white rounded-none border border-[#1C1C1E]/10 p-8 sm:p-10 shadow-xs flex flex-col justify-between">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-xl shrink-0 mt-0.5">
                    <i className="fa-solid fa-bed" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-[#3885B8] block">
                      Bo på behandlingsstedet
                    </span>
                    <h3 className="text-xl sm:text-2xl font-bold text-[#1C1C1E] mt-0.5">
                      Døgnbehandling i Sejstrup
                    </h3>
                  </div>
                </div>

                <div className="space-y-3 text-sm sm:text-base text-[#1C1C1E]/80 leading-relaxed">
                  <p>
                    For voksne fra 18 år, som har behov for en sammenhængende indsats med fuld forplejning, boforhold og 24-timers pædagogisk og sundhedsfaglig støtte.
                  </p>
                  <ul className="space-y-2 pt-2 text-sm text-[#1C1C1E]/85">
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#3885B8] mt-1 shrink-0 text-xs" aria-hidden="true" />
                      <span>Fuld afstand til vante miljøer og fristelser</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#3885B8] mt-1 shrink-0 text-xs" aria-hidden="true" />
                      <span>Faste dagsrutiner, sund kost, motion og fællesskab</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#3885B8] mt-1 shrink-0 text-xs" aria-hidden="true" />
                      <span>Intensivt individuelt og gruppebaseret terapeutisk arbejde</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="pt-8 mt-6 border-t border-[#1C1C1E]/10 flex flex-wrap items-center justify-between gap-4">
                <Link
                  to="/doegnbehandling"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#3885B8] text-white text-xs sm:text-sm font-bold hover:bg-[#2A6E9B] transition-colors"
                >
                  <span>Læs mere om døgnbehandling</span>
                  <i className="fa-solid fa-arrow-right text-[11px]" aria-hidden="true" />
                </Link>
                <Link
                  to="/midtgaarden-i-sejstrup"
                  className="text-xs font-semibold text-[#1C1C1E]/70 hover:text-[#3885B8] transition-colors"
                >
                  Se faciliteterne i Sejstrup →
                </Link>
              </div>
            </div>

            {/* Card 2: Dagbehandling */}
            <div className="bg-white rounded-none border border-[#1C1C1E]/10 p-8 sm:p-10 shadow-xs flex flex-col justify-between">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-xl shrink-0 mt-0.5">
                    <i className="fa-solid fa-sun" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-[#3885B8] block">
                      Fleksibel struktur
                    </span>
                    <h3 className="text-xl sm:text-2xl font-bold text-[#1C1C1E] mt-0.5">
                      Når dagbehandling passer bedre til hverdagen
                    </h3>
                  </div>
                </div>

                <div className="space-y-3 text-sm sm:text-base text-[#1C1C1E]/80 leading-relaxed">
                  <p>
                    Døgnbehandling er ikke nødvendigvis den rette ramme for alle. Dagbehandling kan være relevant for borgere, som har brug for et struktureret behandlingstilbud, men som ikke skal bo på Midtgaarden.
                  </p>
                  <p className="text-sm">
                    Valget mellem dagbehandling og døgnbehandling afhænger blandt andet af den konkrete situation, støttebehovet, stabiliteten i hverdagen og de rammer, der bedst kan understøtte forandring.
                  </p>
                  <ul className="space-y-2 pt-2 text-sm text-[#1C1C1E]/85">
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#3885B8] mt-1 shrink-0 text-xs" aria-hidden="true" />
                      <span>Obligatorisk fremmøde 4-5 dage om ugen i hverdagene</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#3885B8] mt-1 shrink-0 text-xs" aria-hidden="true" />
                      <span>DAT-gruppe, primærgruppe og tilbagefaldsforebyggelse</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#3885B8] mt-1 shrink-0 text-xs" aria-hidden="true" />
                      <span>Mulighed for at bo i egen bolig eller i Halvvejshus</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="pt-8 mt-6 border-t border-[#1C1C1E]/10 flex flex-wrap items-center justify-between gap-4">
                <Link
                  to="/behandling/dagbehandling"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#3885B8] text-white text-xs sm:text-sm font-bold hover:bg-[#2A6E9B] transition-colors"
                >
                  <span>Læs mere om dagbehandling</span>
                  <i className="fa-solid fa-arrow-right text-[11px]" aria-hidden="true" />
                </Link>
                <Link
                  to="/priser"
                  className="text-xs font-semibold text-[#1C1C1E]/70 hover:text-[#3885B8] transition-colors"
                >
                  Se takster for dagbehandling →
                </Link>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* SECTION 5: ET INDIVIDUELT BEHANDLINGSFORLØB */}
      <section className="py-16 sm:py-24 bg-white border-b border-[#1C1C1E]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            
            {/* Visual element / photo */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="rounded-none overflow-hidden border border-[#1C1C1E]/10 shadow-xl bg-white">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/mand-i-samtale-i-laenestol-under-ovenlys.jpg"
                  alt="Mand i samtale siddende i en lænestol under et ovenlysvindue"
                  className="w-full h-56 object-cover rounded-none"
                />
                <div className="p-4 bg-white rounded-none border-t border-[#1C1C1E]/10">
                  <p className="text-xs text-[#1C1C1E]/70 flex items-start gap-2">
                    <i className="fa-solid fa-user-check text-[#3885B8] mt-0.5 shrink-0" aria-hidden="true" />
                    <span>Behandlingen tilpasses løbende den enkelte borgers funktionsniveau, ressourcer og udvikling.</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Prose content */}
            <div className="lg:col-span-7 order-1 lg:order-2 space-y-6">
              <div>
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8] block mb-2">
                  ERFARING & TILPASNING
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
                  Et individuelt behandlingsforløb
                </h2>
                <div className="w-16 h-1 bg-[#3885B8] my-4" />
              </div>

              <div className="space-y-4 text-base sm:text-lg text-[#1C1C1E]/80 leading-relaxed pt-1">
                <p>
                  Et forløb hos Midtgaarden begynder med at skabe overblik over misbruget, de psykiske udfordringer og den støtte, der er brug for. Vi ser på biologiske, psykiske og sociale forhold og planlægger en indsats, der kan rumme både behandling, struktur og en realistisk vej videre.
                </p>
                <p>
                  Med mere end 20 års erfaring arbejder vi med differentierede forløb for mennesker med stofmisbrug, alkoholmisbrug og samtidige problemstillinger. Det betyder, at behandlingen kan tilpasses undervejs, hvis behovene ændrer sig.
                </p>
                <p>
                  Målet er ikke at finde én enkel løsning på komplekse problemer, men at skabe et fagligt og sammenhængende forløb, der kan give den enkelte bedre mulighed for at håndtere sin situation og skabe en mere stabil hverdag.
                </p>
              </div>

              {/* Three key pillars */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
                <div className="bg-[#F8FAFC] border border-[#1C1C1E]/10 p-4 rounded-none space-y-1 shadow-2xs">
                  <div className="w-8 h-8 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-sm mb-2">
                    <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
                  </div>
                  <h4 className="font-bold text-sm text-[#1C1C1E]">1. Grundigt overblik</h4>
                  <p className="text-xs text-[#1C1C1E]/70">Afklaring af misbrug, psykisk sårbarhed og ressourcer.</p>
                </div>

                <div className="bg-[#F8FAFC] border border-[#1C1C1E]/10 p-4 rounded-none space-y-1 shadow-2xs">
                  <div className="w-8 h-8 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-sm mb-2">
                    <i className="fa-solid fa-sliders" aria-hidden="true" />
                  </div>
                  <h4 className="font-bold text-sm text-[#1C1C1E]">2. Differentiering</h4>
                  <p className="text-xs text-[#1C1C1E]/70">Løbende tilpasning af metoder, tempo og støttebehov.</p>
                </div>

                <div className="bg-[#F8FAFC] border border-[#1C1C1E]/10 p-4 rounded-none space-y-1 shadow-2xs">
                  <div className="w-8 h-8 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-sm mb-2">
                    <i className="fa-solid fa-seedling" aria-hidden="true" />
                  </div>
                  <h4 className="font-bold text-sm text-[#1C1C1E]">3. Holdbar hverdag</h4>
                  <p className="text-xs text-[#1C1C1E]/70">Udslusning og efterværn i tæt samarbejde med kommune.</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 6: FÅ EN FAGLIG SAMTALE OM NÆSTE SKRIDT / KONTAKT WEBFORM */}
      <section className="relative py-20 sm:py-28 bg-[#1C1C1E] text-white overflow-hidden">
        {/* Full-bleed background image with dark overlay */}
        <img
          src="https://assets.auxodev.dk/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/loungeomraade-med-laenestole-under-skraavaegge.jpg"
          alt="Loungeområde med sorte lænestole og sofabord under skråvægge med synlige bjælker"
          className="absolute inset-0 w-full h-full object-cover opacity-60 rounded-none"
        />
        <div className="absolute inset-0 bg-[#1C1C1E]/60 backdrop-blur-xs" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Column: Heading, text, buttons, links */}
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#3885B8]">
                RÅDGIVNING & VISITATION
              </span>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight mb-4">
                Få en faglig samtale om næste skridt
              </h2>
              <div className="w-16 h-1 bg-[#3885B8] my-4" />

              <div className="space-y-3 text-base sm:text-lg text-white/90 leading-relaxed font-normal">
                <p>
                  Har du brug for at få hjælp til at komme ud af misbrug, eller er du i kontakt med en, som har det? Så er du meget velkommen til at ringe hertil på telefon{' '}
                  <a
                    href={COMPANY_INFO.phoneTel}
                    className="text-white font-medium underline underline-offset-2 hover:text-[#3885B8] transition-colors whitespace-nowrap"
                  >
                    {COMPANY_INFO.phone}
                  </a>{' '}
                  eller sende en e-mail til{' '}
                  <a
                    href={COMPANY_INFO.emailMailto}
                    className="text-white font-medium underline underline-offset-2 hover:text-[#3885B8] transition-colors whitespace-nowrap"
                  >
                    {COMPANY_INFO.email}
                  </a>{' '}
                  og høre mere om døgnbehandling og Midtgaarden generelt.
                </p>
                <p>
                  Det kan være svært at vide, hvilken hjælp der passer, når psykiske udfordringer og misbrug optræder samtidig. Hos Midtgaarden kan borgere, pårørende og fagpersoner få information om mulighederne og tage en faglig samtale om, hvad næste skridt kan være.
                </p>
                <p className="font-medium text-white">
                  Midtgaarden ligger i Sejstrup nær Esbjerg og tager imod borgere fra hele landet.
                </p>
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <a
                  href={COMPANY_INFO.phoneTel}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#3885B8] hover:bg-[#2A6E9B] text-white font-semibold text-sm transition-all shadow-md"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span>Ring: {COMPANY_INFO.phone}</span>
                </a>

                <a
                  href={COMPANY_INFO.emailMailto}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/30 text-white/90 font-semibold text-sm hover:border-white hover:text-white transition-all backdrop-blur-xs"
                >
                  <i className="fa-solid fa-envelope" aria-hidden="true" />
                  <span>{COMPANY_INFO.email}</span>
                </a>
              </div>

            </div>

            {/* Right Column: Webform structured vertically */}
            <div className="lg:col-span-6 bg-white/5 p-8 rounded-none border border-white/15 backdrop-blur-md shadow-2xl">
              <h3 className="text-xl font-bold text-white mb-6">Send en besked</h3>

              {formSubmitted ? (
                <div className="p-6 bg-[#3885B8]/20 border border-[#3885B8] rounded-none text-center space-y-3">
                  <i className="fa-solid fa-circle-check text-3xl text-[#3885B8]" aria-hidden="true" />
                  <h4 className="text-lg font-bold text-white">Mange tak for din henvendelse</h4>
                  <p className="text-xs text-white/80">Vi har modtaget din besked og vender tilbage hurtigst muligt.</p>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit} className="space-y-6">
                  
                  {/* Honeypot Antispam Field */}
                  <div className="sr-only" aria-hidden="true">
                    <label htmlFor="website_url">Website URL</label>
                    <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                  </div>

                  {/* Top row 50/50 grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-white/70 mb-1">
                        Navn *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        placeholder="Dit navn"
                        className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/30 focus:outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-white/70 mb-1">
                        Telefonnummer *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        placeholder="Dine 8 cifre"
                        className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/30 focus:outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Email field */}
                  <div>
                    <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-white/70 mb-1">
                      E-mailadresse *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      placeholder="din@email.dk"
                      className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/30 focus:outline-none transition-colors"
                    />
                  </div>

                  {/* Description / Message field */}
                  <div>
                    <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wider text-white/70 mb-1">
                      Beskrivelse *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows="4"
                      required
                      placeholder="Skriv din besked eller spørgsmål om dobbeltdiagnose her..."
                      className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/30 focus:outline-none transition-colors resize-none"
                    ></textarea>
                  </div>

                  {formError && (
                    <p className="text-xs font-semibold text-rose-400">{formError}</p>
                  )}

                  {/* Full-width pill-shaped primary CTA button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 px-6 rounded-full bg-[#3885B8] hover:bg-[#2A6E9B] text-white font-bold text-sm tracking-wider uppercase transition-colors shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3885B8] disabled:opacity-50 cursor-pointer"
                  >
                    {submitting ? 'Sender henvendelse...' : 'Send henvendelse'}
                  </button>
                </form>
              )}
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
